import {
  EnrollementQuestionnaireSchema,
  FormFieldDto,
} from '../../interface/enrollmentApplication/EnrollementQuestionnaireSchema'
import {
  getDisabledSections,
  getUnderwritingType,
  getVisibleFields,
  getVisibleSections,
  isWithinLastSixMonths,
  processFieldEvents,
  transformDataForSubmission,
  turnedSixtyFiveInLastSixMonths,
  validateField,
} from '../../utils/EnrollmentApplicationFormUtils'
import { useCallback, useMemo, useState } from 'react'

import { SnackbarTypes } from '../../enums/SnackbarTypesEnum'
import dayjs from 'dayjs'
import snackbarStore from '../../datastore/SnackbarStore'

export const useFormState = (
  schema: EnrollementQuestionnaireSchema,
  initialValues: Record<string, any>
) => {
  const [formData, setFormData] = useState<{ [key: string]: any }>(
    initialValues
  )
  const [errors, setErrors] = useState<{ [key: string]: any }>({})
  const [currentStep, setCurrentStep] = useState(0)

  // Get visible sections based on dependencies
  const visibleSections = useMemo(() => {
    return getVisibleSections(
      schema.sections,
      formData,
      getDisabledSections(true)
    )
  }, [schema.sections, formData])

  // Enhanced update field with event processing
  const updateField = useCallback(
    (fieldId, value) => {
      setFormData((prev) => {
        const newData: { [key: string]: any } = { ...prev, [fieldId]: value }

        // explicit side effects
        if (fieldId === 'applicant_dob') {
          if (turnedSixtyFiveInLastSixMonths(dayjs(value).toDate())) {
            newData['did_turn_65_in_last_six_mo'] = true
          } else {
            newData['did_turn_65_in_last_six_mo'] = false
          }
          newData['underwriting_type'] = getUnderwritingType({
            dateOfBirth: dayjs(value).toDate(),
            medicarePartBEffectiveDate: prev['medicare_part_b_eff_date']
              ? dayjs(prev['medicare_part_b_eff_date']).toDate()
              : undefined,
            guaranteeIssue: prev['apply_guaranteed_issue'] || false,
          })
        }

        if (fieldId === 'medicare_part_b_eff_date') {
          if (isWithinLastSixMonths(dayjs(value).toDate())) {
            newData['enroll_part_b_last_6_mo'] = true
          } else {
            newData['enroll_part_b_last_6_mo'] = false
          }
          newData['underwriting_type'] = getUnderwritingType({
            dateOfBirth: prev['applicant_dob']
              ? dayjs(prev['applicant_dob']).toDate()
              : undefined,
            medicarePartBEffectiveDate: dayjs(value).toDate(),
            guaranteeIssue: prev['apply_guaranteed_issue'] || false,
          })
        }

        if (fieldId === 'apply_guaranteed_issue') {
          newData['underwriting_type'] = getUnderwritingType({
            dateOfBirth: prev['applicant_dob']
              ? dayjs(prev['applicant_dob']).toDate()
              : undefined,
            medicarePartBEffectiveDate: prev['medicare_part_b_eff_date']
              ? dayjs(prev['medicare_part_b_eff_date']).toDate()
              : undefined,
            guaranteeIssue: value || false,
          })
        }

        // Find the field to check for trigger events
        const currentSection = visibleSections[currentStep]
        const field = currentSection?.body.find((f) => f.id === fieldId)
        const allFields = visibleSections.flatMap((s) => s.body)

        // Process trigger events
        if (field?.trigger_events) {
          processFieldEvents(
            field,
            value,
            newData,
            (id: string | number, val: any) => {
              newData[id as string] = val
            },
            allFields
          )
        }

        return newData
      })

      // Clear error when field is updated, but also run validation for date fields
      if (errors[fieldId]) {
        setErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[fieldId]
          return newErrors
        })
      }

      // Run immediate validation for date fields
      const currentSection = visibleSections[currentStep]
      const field = currentSection?.body.find((f) => f.id === fieldId)
      if (field?.type === 'complex_date_picker_field' && value) {
        const fieldErrors = validateField(field, value, {
          ...formData,
          [fieldId]: value,
        })
        if (fieldErrors.length > 0) {
          setErrors((prev) => ({ ...prev, [fieldId]: fieldErrors[0] }))
        }
      }
    },
    [errors, visibleSections, currentStep]
  )

  // Advanced validation with custom rules
  const validateStep = useCallback(() => {
    const currentSection = visibleSections[currentStep]
    if (!currentSection) return { isValid: true, stepErrors: {} }

    const visibleFields = getVisibleFields(currentSection, formData)
    const stepErrors: Record<string, string> = {}

    visibleFields.forEach((field: FormFieldDto) => {
      const fieldErrors = validateField(field, formData[field.id], formData)
      if (fieldErrors.length > 0) {
        stepErrors[field.id] = fieldErrors[0] // Take first error
      }
    })

    setErrors(stepErrors)

    const errorsLength = Object.keys(stepErrors).length
    if (errorsLength > 0) {
      snackbarStore.set({
        snackbarMessage: `Please resolve ${
          Object.keys(stepErrors).length
        } errors in this step`,
        snackbarOpen: true,
        snackbarType: SnackbarTypes.WARNING,
      })
    }
    return { 
      isValid: Object.keys(stepErrors).length === 0,
      stepErrors 
    }
  }, [currentStep, visibleSections, formData])

  // Data transformation for submission
  const getTransformedData = useCallback(() => {
    return transformDataForSubmission(formData, schema.sections)
  }, [formData, schema.sections])

  return {
    formData,
    errors,
    currentStep,
    setCurrentStep,
    visibleSections,
    getVisibleFields,
    updateField,
    validateStep,
    getTransformedData,
  }
}

export default useFormState
